import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/auto-docking-simulation-maritime/HeaderImage.webp';
import AutoDockingRequirementsImage from 'assets/resources/auto-docking-simulation-maritime/AutoDockingRequirementsImage.webp';
import ValidationImage from 'assets/resources/auto-docking-simulation-maritime/ValidationImage.webp';
import { BOOK_A_DEMO_LINK } from 'constants/common';

const commonData = {
  ...AUTHORS.AILiveSim,
  title: 'Why Simulation Matters for Maritime Autonomy: Accelerating Auto-Docking AI with AILiveSim',
  publishDate: 'January 1, 2026',
  minutesOfRead: 10,
};

export const firstDecemberAutoDocking2026ArticleMeta: PageMeta = {
  url: '/articles/auto-docking-simulation-maritime',
  title: commonData.title,
  description:
    'AI-powered auto-docking technology promises to transform maritime operations, but only if algorithms can be trained and validated at scale. Discover how simulation becomes indispensable.',
};

export const firstDecemberAutoDocking2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          Docking a vessel remains one of the most challenging maneuvers in boating. Insurance
          claims consistently show that a significant percentage of recreational boating incidents
          occur during berthing, where misjudged distances, unpredictable wind gusts, and crowded
          marinas create the perfect conditions for costly collisions.
        </p>
        <p>
          For naval and commercial operators, the stakes rise even higher when autonomous systems
          must perform these maneuvers reliably under mission-critical conditions. AI-powered
          auto-docking technology promises to transform this reality, but only if the underlying
          algorithms can be trained and validated at scale.
        </p>
        <p>That&apos;s where simulation becomes indispensable.</p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: 'What Auto-Docking AI Requires',
    image: AutoDockingRequirementsImage,
    text: (
      <>
        <p>
          Auto-docking systems combine sensor fusion—cameras, radar, lidar, positioning—with control
          algorithms that translate environmental understanding into precise vessel maneuvering.
        </p>
        <p>
          The AI interprets conditions continuously, adapting approach paths to wind, waves, and
          obstacles. Done right, these systems reduce the berthing incidents that frustrate boat
          owners and drive insurance claims.
        </p>
        <p>
          The technology has matured fast. Consumer solutions that were demonstrated two years ago
          are now shipping products. Naval programs are advancing autonomous berthing for
          operational advantages.
        </p>
        <p>
          But reliability depends on validation—testing across the full range of conditions systems
          will encounter. Physical testing, with its costs, weather dependencies, and safety
          constraints, can only cover a fraction of what&apos;s needed.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'How AILiveSim Enables Comprehensive Validation',
    image: ValidationImage,
    text: (
      <>
        <p>
          Our platform brings simulation directly into development workflows. Closed-loop testing
          across ten sensor types means algorithms run as they will in deployment—not watching
          recorded scenarios but actively operating in an environment that responds to their
          decisions. When your control algorithm commands a thruster, the simulated vessel moves,
          sensors update, and the next decision cycle begins.
        </p>
        <p>
          <strong>Intelligent System Testing (IST)</strong>, a technology AILiveSim enables for
          customers, automates the search for problems. IST explores parameter combinations
          systematically, finding the specific conditions where algorithms break.
        </p>
        <p>
          Variations and unusual environmental combinations, such as wind and hard rain, IST can
          identify these edge cases before deployment does.
        </p>
        <p>
          These validation capabilities depend on sensor fidelity, which is why our recent platform
          updates have focused on camera and radar simulation improvements.
        </p>
        <p>
          Based directly on customer feedback, we&apos;ve enhanced our physics-based IR camera
          modeling and expanded radar accuracy to deliver the synthetic training data quality that
          rigorous algorithm development demands.
        </p>
        <p>
          As we continue advancing sensor technology across the platform, each improvement
          strengthens the realism of closed-loop testing and expands the edge cases IST can
          meaningfully explore.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Customer Proof Point: Maritime OEM Success',
    text: (
      <>
        <p>
          A leading maritime OEM developing AI-powered auto-berthing for leisure boats faced a
          common constraint: validation campaigns required deploying an eight-person team for
          week-long field operations, costing tens of thousands of euros per cycle.
        </p>
        <p>
          Weather dependencies created unpredictable schedules, and safety limitations restricted
          early-stage testing scope.
        </p>
        <p>
          After integrating AILiveSim, the OEM replaced field-dependent validation with lab-based
          simulation that covered more scenarios at lower cost and zero safety risk. High-fidelity
          marine physics enabled testing across multiple marina configurations, including complex
          double-twin berth layouts.
        </p>
        <p>
          Edge cases, such as emergency vessels, floating debris, sensor degradation, became
          standard test coverage rather than untestable risks.
        </p>
        <p>
          <em>
            The outcome: an industry-first intelligent co-captain solution delivered faster and
            validated more thoroughly than traditional development approaches would permit.
          </em>
        </p>
        <blockquote>
          &quot;Simulation allowed us to iterate quickly, validate safely, and deliver an
          intelligent co-captain experience the market hadn&apos;t seen before,&quot; confirmed the
          OEM&apos;s Innovation Lead.
        </blockquote>
        <p>
          <strong>Resources:</strong>
        </p>
        <ul>
          <li>
            Read our White Paper about the sim to real gap:{' '}
            <a
              href="https://ailivesim.com/whitepapers/using-synthetic-data-from-ailivesim"
              target="_blank"
              rel="noopener noreferrer"
            >
              Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models
            </a>
          </li>
          <li>
            Read our Autodocking Customer Case:{' '}
            <a
              href="https://ailivesim.com/customer-case/ai-powered-auto-docking"
              target="_blank"
              rel="noopener noreferrer"
            >
              AI Auto-Docking for Leisure Boats
            </a>
          </li>
        </ul>
      </>
    ),
  },

  {
    type: 'common',
    title: 'What This Means Financially',
    text: (
      <>
        <p>
          The numbers tell the story. Tens of thousands saved per eliminated field campaign.
          Development cycles reduced up to 60%. Training time compressed from weeks to hours through
          cloud-based parallel simulation.
        </p>
        <p>
          Engineering hours redirected from logistics to product development. Physical testing
          shifts from primary validation method to supplementary confirmation. The result: faster
          time-to-market with better-validated products.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Start Your Transformation',
    text: (
      <>
        <p>
          Whether you&apos;re building auto-docking or other autonomous functionalities for
          recreational, commercial, or defense applications, simulation provides the path to
          trustworthy autonomy.
        </p>
        <p>
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Schedule a demo session with us to learn more.
            </a>
          </strong>
        </p>
      </>
    ),
  },
];

export const firstDecemberAutoDocking2026ArticleCard: TCardProps = {
  description:
    'This article shows how simulation trains auto-docking AI using synthetic sensor data. One OEM replaced costly field tests with lab simulations, covering more edge cases. Development time dropped 60% while improving validation.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: firstDecemberAutoDocking2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.AITraining,
    EHashtag.AutonomousVehicles,
    EHashtag.MachineLearning,
    EHashtag.AISimulation,
    EHashtag.MaritimeTechnology,
  ],
};
